import { Divider, type SelectChangeEvent, Stack } from '@mui/material';
import { FlowDurationUnits } from 'flow-schema';

import { isDefined, isTruthy } from 'common';

import { getDefaultServiceReviewConfig } from './utils';
import { FeatureFlagKey, useFlag } from '../../../../../../../utils/flags';
import { useReactFlowViewContext } from '../../../context/context';
import {
  BlockValueTemplateField,
  BlockValueTemplateFieldArea,
} from '../../ConfigFields/CodeMirror/FlowTemplate';
import { BlockDurationField } from '../../ConfigFields/Duration';
import { BlockSelectField } from '../../ConfigFields/SelectField';
import { BlockSwitchField } from '../../ConfigFields/Switch';
import { useSelectedNodeField } from '../../ConfigFields/useSelectedNodeField';

enum TransactionType {
  Authorization = 'Authorization',
  NoticeOfAdmission = 'NoticeOfAdmission',
  ClinicalDocuments = 'ClinicalDocuments',
}

enum PayorPortal {
  Availity = 'Availity',
  EviCore = 'EviCore',
  PNTData = 'PNT Data',
}

const TRANSACTION_TYPE_LABELS: Record<TransactionType, string> = {
  [TransactionType.Authorization]: 'Authorization',
  [TransactionType.NoticeOfAdmission]: 'Notice of Admission',
  [TransactionType.ClinicalDocuments]: 'Clinical Documents',
};

enum SubmissionMode {
  API = 'API',
  SubmissionMode278 = '278',
}

const TransactionTypeToPayorPortal: Record<TransactionType, PayorPortal[]> = {
  [TransactionType.Authorization]: [PayorPortal.Availity, PayorPortal.PNTData, PayorPortal.EviCore],
  [TransactionType.NoticeOfAdmission]: [
    PayorPortal.Availity,
    PayorPortal.PNTData,
    PayorPortal.EviCore,
  ],
  [TransactionType.ClinicalDocuments]: [PayorPortal.EviCore],
};

const payorPortalBySubmissionModeMapping: Record<SubmissionMode, PayorPortal[]> = {
  [SubmissionMode.API]: [PayorPortal.Availity],
  [SubmissionMode.SubmissionMode278]: [PayorPortal.EviCore],
};

const payorPortalBySubmissionModeMappingWithPntData: Record<SubmissionMode, PayorPortal[]> = {
  [SubmissionMode.API]: [PayorPortal.Availity],
  [SubmissionMode.SubmissionMode278]: [PayorPortal.EviCore, PayorPortal.PNTData],
};

export function ServiceReviewFormFields() {
  const { value: transactionType, setValue: setTransactionType } =
    useSelectedNodeField<TransactionType | null>('config.transactionType');
  const { value: submissionMode } = useSelectedNodeField<SubmissionMode | null>(
    'config.submissionMode'
  );

  const enablePntDataSubmission = useFlag({ flagKey: FeatureFlagKey.enablePntDataSubmission });
  const { selectedNodeId, reactFlowMutators } = useReactFlowViewContext();

  // Defaults transactionType to Authorization for compatibility with the old block
  const effectiveTransactionType = transactionType ?? TransactionType.Authorization;

  return (
    <Stack gap={4}>
      <BlockSelectField
        fieldName="config.transactionType"
        label="Transaction Type"
        options={Object.values(TransactionType).map((method) => ({
          label: TRANSACTION_TYPE_LABELS[method],
          value: method,
        }))}
        onChange={(event: SelectChangeEvent<TransactionType>) => {
          const newValue = event.target.value;

          if (!isTruthy(newValue) && isDefined(setTransactionType)) {
            setTransactionType(TransactionType.Authorization);
          }

          if (isDefined(selectedNodeId)) {
            reactFlowMutators?.updateNodeData(selectedNodeId, (node) => {
              const existingConfig =
                'config' in node.data &&
                typeof node.data.config === 'object' &&
                isDefined(node.data.config)
                  ? (node.data.config as Record<string, unknown>)
                  : {};
              return {
                ...node.data,
                config: {
                  ...existingConfig,
                  ...getDefaultServiceReviewConfig(),
                },
              };
            });
          }
        }}
        clearable={false}
        defaultValue={TransactionType.Authorization}
      />
      {(effectiveTransactionType === TransactionType.Authorization ||
        effectiveTransactionType === TransactionType.NoticeOfAdmission) && (
        <>
          <Divider />
          <BlockSelectField
            fieldName="config.submissionMode"
            label="Submission Mode"
            options={Object.values(SubmissionMode).map((method) => ({
              label: method,
              value: method,
            }))}
            required
          />
          {isDefined(submissionMode) &&
            isDefined(payorPortalBySubmissionModeMapping[submissionMode]) && (
              <BlockSelectField
                fieldName="config.payorPortal"
                label="Payor Portal"
                options={Object.values(
                  enablePntDataSubmission.enablePntDataSubmission
                    ? payorPortalBySubmissionModeMappingWithPntData[submissionMode]
                    : payorPortalBySubmissionModeMapping[submissionMode]
                ).map((method) => ({
                  label: method,
                  value: method,
                }))}
                required
              />
            )}
          <BlockValueTemplateField
            fieldName="config.patientOrder"
            label="Patient Order"
            helperText="Pass in patient order from an upstream block"
          />
          <BlockValueTemplateField
            fieldName="config.patientInsuranceId"
            label="Patient Insurance ID"
          />
          <BlockValueTemplateFieldArea
            fieldName="config.customPayload"
            label="Custom Payload"
            helperText="Configure your own payload"
          />
        </>
      )}
      {effectiveTransactionType === TransactionType.ClinicalDocuments && (
        <>
          <Divider />
          {isDefined(TransactionTypeToPayorPortal[effectiveTransactionType]) && (
            <BlockSelectField
              fieldName="config.payorPortal"
              label="Payor Portal"
              options={Object.values(TransactionTypeToPayorPortal[effectiveTransactionType]).map(
                (method) => ({
                  label: method,
                  value: method,
                })
              )}
              required
            />
          )}
          <BlockValueTemplateField
            fieldName="config.fileReference"
            label="File Reference"
            placeholder="e.g. {{getDocument.fileReference}}"
          />
          <BlockValueTemplateField
            fieldName="config.caseId"
            label="Evicore Case ID"
            placeholder="e.g. {{serviceReviewSubmission.data.caseId}}"
          />
          <BlockValueTemplateField
            fieldName="config.description"
            label="Description (optional)"
            placeholder="e.g. {{getDocument.description}}"
          />
        </>
      )}
      <BlockDurationField
        fieldName="config.timeout"
        unitFieldNameSuffix="units"
        required={true}
        allowedUnits={[
          FlowDurationUnits.Hours,
          FlowDurationUnits.Minutes,
          FlowDurationUnits.Seconds,
        ]}
        toggleLabel="Timeout"
        valueLabel="Timeout (fail after duration)"
      />
      <BlockSwitchField
        fieldName="config.isTest"
        label="Send as Test Submission"
        helperText="Send to test environment instead of production"
      />
    </Stack>
  );
}
